import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "model";
import { createSelector } from "reselect";

export type SubmittedActionEntry = {
  deadline: string;
  categoryId: number;
  category: string;
  actionId: number;
  action: string;
  requiredEvidence: string;
  programmeId: string;
  date: string;
  email: string;
  detailQuestion: string;
  submittedDetails: string;
};

class SubmittedActionsAPI {
  async getByEmail(email: string): Promise<Array<SubmittedActionEntry>> {
    const res = await fetch(
      `${
        process.env.REACT_APP_API_ENDPOINT
      }/v1/submittedActions?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    const resultJson = await res.json();
    return resultJson as SubmittedActionEntry[];
  }
}

const submittedActionsApi = new SubmittedActionsAPI();

export const fetchSubmittedActionsByEmail = createAsyncThunk(
  "submittedActions/fetchByEmail",
  async (email: string) => {
    const fetchedEntries = await submittedActionsApi.getByEmail(email);
    return fetchedEntries;
  }
);

const submittedActionsAdapter = createEntityAdapter<SubmittedActionEntry>({
  selectId: (entry) =>
    `${entry.programmeId}-${entry.actionId}-${entry.categoryId}`,
});

export const {
  selectAll: selectAllSubmittedActionsEntries,
  selectById: selectSubmittedActionEntryById,
  selectEntities: selectSubmittedActionsEntriesEntities,
  selectIds: selectSubmittedActionEntryIds,
} = submittedActionsAdapter.getSelectors(
  (state: RootState) => state.submittedActions
);

const selectProgrammeId = (state: RootState, programmeId: string) =>
  programmeId;

export const selectSubmittedActionsEntriesByProgrammeId = createSelector(
  [selectAllSubmittedActionsEntries, selectProgrammeId],
  (entries: SubmittedActionEntry[], programmeId: string) => {
    return entries.filter((entry) => entry.programmeId === programmeId);
  }
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type SubmittedActionsState = EntityState<SubmittedActionEntry> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialSubmittedActionsState: SubmittedActionsState =
  submittedActionsAdapter.getInitialState({
    loading: "pending",
    error: null,
    status: null,
  });

const submittedActionsSlice = createSlice({
  name: "submittedActions",
  initialState: initialSubmittedActionsState,
  reducers: {
    resetSubmittedActions: () => initialSubmittedActionsState,
  },
  extraReducers: {
    [fetchSubmittedActionsByEmail.fulfilled.type]: (state, { payload }) => {
      for (const entry of payload) {
        submittedActionsAdapter.upsertOne(state, {
          deadline: entry.deadline,
          categoryId: entry.categoryId,
          category: entry.category,
          actionId: entry.actionId,
          action: entry.action,
          requiredEvidence: entry.requiredEvidence,
          programmeId: entry.programmeId,
          date: entry.date,
          email: entry.email,
          detailQuestion: entry.detailQuestion,
          submittedDetails: entry.submittedDetails,
        });
      }
      state.loading = "idle";
    },
    [fetchSubmittedActionsByEmail.pending.type]: (state) => {
      state.loading = "pending";
    },
    [fetchSubmittedActionsByEmail.rejected.type]: (state, action) => {
      state.loading = "idle";
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      state.error = action.error as any;
    },
  },
});

const { actions, reducer } = submittedActionsSlice;

export const { resetSubmittedActions } = actions;
export const submittedActionsReducer = reducer;
